import {RefObject, useLayoutEffect, useState} from 'react'

const focusableSelector =
  'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Keeps the existing chat mounted while it covers the mobile report. */
export function useMobileAssistantPresentation(
  isMobile: boolean,
  isVisible: boolean,
  isExpanded: boolean,
  shellRef: RefObject<HTMLElement>,
  closeRef: RefObject<HTMLButtonElement>,
): HTMLElement | null {
  const [launcherHost, setLauncherHost] = useState<HTMLElement | null>(null)

  useLayoutEffect(() => {
    setLauncherHost(
      isMobile && isVisible
        ? document.getElementById('mobile-assistant-launcher')
        : null,
    )
  }, [isMobile, isVisible])

  useLayoutEffect(() => {
    const shell = shellRef.current
    if (!isMobile || !isVisible || !isExpanded || !shell) return

    const background: {element: Element; wasInert: boolean}[] = []
    let branch: Element = shell
    while (branch.parentElement) {
      const parent = branch.parentElement
      Array.from(parent.children).forEach(element => {
        if (element === branch) return
        background.push({element, wasInert: element.hasAttribute('inert')})
        element.setAttribute('inert', '')
      })
      if (parent.id === 'main-layout') break
      branch = parent
    }

    // Opening chat should not summon the software keyboard. Preserve an
    // already focused composer when crossing the responsive boundary.
    const frame = requestAnimationFrame(() => {
      if (!shell.contains(document.activeElement)) {
        closeRef.current?.focus({preventScroll: true})
      }
    })
    const containTab = (event: KeyboardEvent) => {
      // A result table can open its own body-level dialog and focus trap.
      if (event.key !== 'Tab' || !shell.contains(document.activeElement)) return
      const controls = Array.from(
        shell.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter(element => element.getClientRects().length > 0)
      const first = controls[0]
      const last = controls.at(-1)
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    document.addEventListener('keydown', containTab)

    const viewport = window.visualViewport
    const updateViewport = () => {
      // Follow the visible keyboard area at normal scale. Pinch zoom keeps
      // its native layout instead of shrinking the entire chat to the zoom.
      if (viewport?.scale === 1) {
        shell.style.setProperty('--mobile-chat-height', `${viewport.height}px`)
        shell.style.setProperty('--mobile-chat-top', `${viewport.offsetTop}px`)
      } else {
        shell.style.removeProperty('--mobile-chat-height')
        shell.style.removeProperty('--mobile-chat-top')
      }
    }
    updateViewport()
    viewport?.addEventListener('resize', updateViewport)
    viewport?.addEventListener('scroll', updateViewport)

    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', containTab)
      viewport?.removeEventListener('resize', updateViewport)
      viewport?.removeEventListener('scroll', updateViewport)
      shell.style.removeProperty('--mobile-chat-height')
      shell.style.removeProperty('--mobile-chat-top')
      background.forEach(({element, wasInert}) => {
        if (!wasInert) element.removeAttribute('inert')
      })
    }
  }, [isMobile, isVisible, isExpanded, shellRef, closeRef])

  return launcherHost
}
